import {
  DocumentationGroupV1,
  DocumentationItemConfigurationV1,
  DocumentationItemConfigurationV2,
  DocumentationPageV1,
  mapByUnique,
} from "@supernova-studio/model";
import { LocalDocsPage, LocalDocsPageGroup } from "../../../sync/types";
import { VersionRoomDocsPage, VersionRoomDocsPageGroup } from "../../../yjs/version-room/types";

type GroupLike = {
  persistentId: string;
  parentPersistentId?: string;
};

type DocsPage = LocalDocsPage | VersionRoomDocsPage;
type DocsPageGroup = LocalDocsPageGroup | VersionRoomDocsPageGroup;

// The fact that DTO conversion is located here instead of main backend code is due to the fact
// that we store page and group data in YJS documents in the same way as they are stored in the database.
// Therefore, we need to expose this conversion to the client so that it can consume data from YJS documents.
//
// Please do not put more DTO conversion here unless you know what you're doing.

export function buildDocPagePublishPaths(
  groups: (DocsPageGroup | DocumentationGroupV1)[],
  pages: (DocsPage | DocumentationPageV1)[],
  routingVersion: string
): Map<string, string> {
  const groupPersistentIdToGroupMap = mapByUnique(groups, group => group.persistentId);
  const result = new Map<string, string>();

  for (const page of pages) {
    const parentChain = calculateElementParentChain(page.parentPersistentId, groupPersistentIdToGroupMap);

    let pathV1 = `${page.userSlug || page.slug}.html`;
    let pathV2 = `${page.slug}-${page.shortPersistentId}`;

    for (const parent of parentChain) {
      if (!parent.parentPersistentId) continue;

      pathV1 = `${parent.userSlug || parent.slug || "root"}/${pathV1}`;
      pathV2 = `${parent.slug || "root"}/${pathV2}`;
    }

    pathV1 = `/${pathV1}`;
    pathV2 = `/${pathV2}`;

    if (routingVersion === "2") {
      result.set(page.persistentId, pathV2);
    } else {
      result.set(page.persistentId, pathV1);
    }
  }

  return result;
}

export function calculateElementParentChain<T extends GroupLike>(
  elementParentPersistentId: string,
  groupPersistentIdToGroupMap: Map<string, T>
): T[] {
  const result: T[] = [];

  let parentId: string | undefined = elementParentPersistentId;
  while (parentId) {
    const parent = groupPersistentIdToGroupMap.get(parentId);
    if (parent) result.push(parent);

    parentId = parent?.parentPersistentId;
  }

  return result;
}

export function applyPrivacyConfigurationToNestedItems<
  PageType extends DocumentationPageV1 | DocsPage,
  GroupType extends DocumentationGroupV1 | DocsPageGroup,
  ItemConfigurationType extends DocumentationItemConfigurationV1 | DocumentationItemConfigurationV2
>(
  pages: PageType[],
  groups: GroupType[],
  getDefaultItemConfiguration: () => ItemConfigurationType
): { pages: PageType[]; groups: GroupType[] } {
  // copy groups to mutate them later
  const fixedGroups = groups.map(group => ({
    ...group,
    data: {
      ...group.data,
      configuration: group.data?.configuration ?? getDefaultItemConfiguration(),
    },
  }));
  const groupPersistentIdToGroupMap = mapByUnique(fixedGroups, group => group.persistentId);

  for (const groupToFix of fixedGroups) {
    let isHidden = groupToFix.data.configuration.isHidden;
    let isPrivate = groupToFix.data.configuration.isPrivate;

    let nextParentId: string | undefined = groupToFix.parentPersistentId;
    while ((!isHidden || !isPrivate) && nextParentId) {
      const parent = groupPersistentIdToGroupMap.get(nextParentId);
      if (!parent) break;

      isHidden = isHidden || parent.data?.configuration?.isHidden || false;
      isPrivate = isPrivate || parent.data?.configuration?.isPrivate || false;

      nextParentId = parent.parentPersistentId;
    }

    groupToFix.data.configuration.isHidden = isHidden;
    groupToFix.data.configuration.isPrivate = isPrivate;
  }

  const fixedPages: PageType[] = [];
  // set isHidden and isPrivate configuration fields based on page and parents value
  for (const page of pages) {
    const configuration = page.data?.configuration ?? getDefaultItemConfiguration();
    const parent = groupPersistentIdToGroupMap.get(page.parentPersistentId);

    fixedPages.push({
      ...page,
      data: {
        ...page.data,
        configuration: {
          ...configuration,
          isHidden: configuration.isHidden || parent?.data?.configuration?.isHidden || false,
          isPrivate: configuration.isPrivate || parent?.data?.configuration?.isPrivate || false,
        },
      },
    });
  }

  return { pages: fixedPages, groups: fixedGroups };
}
